import { Input, Select, Spin } from 'antd';
import './SelectComponent.scss';
import { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import ButtonIconGhostMedium from '../ButtonIconGhost/ButtonIconGhostMedium';
import { Icon } from '../../icon/icon';
import LoadingComponent from '../LoadingComponent/LoadingComponent';
import { Empty } from 'antd';
import { cn } from '../../../utils/string/cn.utils';

const SelectComponent = forwardRef(
  (
    {
      placeholder,
      onChange,
      onSearch,
      options,
      value,
      actionLeft,
      showSearch,
      field,
      style,
      noBorder,
      size = 'large',
      searchIn,
      error,
      disabled,
      required,
      isLoading,
      disabledSorting,
      ...props
    },
    ref
  ) => {
    const [searchValue, setSearchValue] = useState();
    const [sortAToZ, setSortAToZ] = useState(undefined);
    const [open, setOpen] = useState(false);
    const selectRef = useRef(null);

    // Override ref để customize focus behavior
    useImperativeHandle(ref, () => ({
      focus: () => {
        setOpen(true);
        selectRef.current?.focus();
      },
      blur: () => {
        setOpen(false);
        selectRef.current?.blur();
      },
    }));

    return (
      <Select
        ref={selectRef}
        showSearch={showSearch}
        style={{ width: '100%', ...style }}
        placeholder={placeholder}
        optionFilterProp="label"
        onChange={onChange}
        disabled={disabled}
        onSearch={onSearch}
        open={open}
        onOpenChange={setOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' && open) {
            const optionSelected = options?.find(option =>
              option.label?.toLowerCase().includes(searchValue?.toLowerCase())
            );
            onChange?.(optionSelected?.value);
          }
        }}
        options={[
          ...(
            options?.filter(option => {
              if (!showSearch && searchValue) {
                return option.label
                  ?.toLowerCase()
                  .includes(searchValue?.toLowerCase());
              }
              return true;
            }) ?? []
          ).map(option => ({
            ...option,
            className: cn(
              option.disabled &&
                'bg-fills-disable [&_.ant-select-item-option-content]:text-black-low'
            ),
          })),
        ]}
        notFoundContent={
          isLoading ? (
            <LoadingComponent size={24} />
          ) : (
            <div className="flex items-center justify-center overflow-hidden self-stretch">
              <Empty />
            </div>
          )
        }
        size={size}
        getPopupContainer={() => document.body}
        filterSort={
          !disabledSorting && options && options.length
            ? (optionA, optionB) => {
                if (
                  typeof optionA.label === 'string' &&
                  typeof optionB.label === 'string'
                )
                  if (sortAToZ === 'A') {
                    return optionA?.label?.localeCompare(optionB?.label, 'vi', {
                      numeric: true,
                      sensitivity: 'base',
                    });
                  } else if (sortAToZ === 'Z') {
                    return optionB?.label?.localeCompare(optionA?.label, 'vi', {
                      numeric: true,
                      sensitivity: 'base',
                    });
                  }
              }
            : undefined
        }
        {...field}
        defaultActiveFirstOption={false}
        popupMatchSelectWidth={false}
        searchValue={showSearch ? searchValue : undefined}
        listHeight={224}
        value={value}
        className={cn(
          noBorder && 'no__border',
          error ? 'field-error' : '',
          props.className
        )}
        popupRender={menu => {
          if (searchIn)
            return (
              <div className="menu__select__container">
                <div className="header">
                  <Input
                    id={'search'}
                    placeholder={'Tìm kiếm'}
                    className="h-32"
                    style={{ width: '260px' }}
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value);
                    }}
                    prefix={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.3333 17.5L11.0833 12.25C10.6667 12.5833 10.1875 12.8472 9.64583 13.0417C9.10417 13.2361 8.52778 13.3333 7.91667 13.3333C6.40278 13.3333 5.12153 12.809 4.07292 11.7604C3.02431 10.7118 2.5 9.43056 2.5 7.91667C2.5 6.40278 3.02431 5.12153 4.07292 4.07292C5.12153 3.02431 6.40278 2.5 7.91667 2.5C9.43056 2.5 10.7118 3.02431 11.7604 4.07292C12.809 5.12153 13.3333 6.40278 13.3333 7.91667C13.3333 8.52778 13.2361 9.10417 13.0417 9.64583C12.8472 10.1875 12.5833 10.6667 12.25 11.0833L17.5 16.3333L16.3333 17.5ZM7.91667 11.6667C8.95833 11.6667 9.84375 11.3021 10.5729 10.5729C11.3021 9.84375 11.6667 8.95833 11.6667 7.91667C11.6667 6.875 11.3021 5.98958 10.5729 5.26042C9.84375 4.53125 8.95833 4.16667 7.91667 4.16667C6.875 4.16667 5.98958 4.53125 5.26042 5.26042C4.53125 5.98958 4.16667 6.875 4.16667 7.91667C4.16667 8.95833 4.53125 9.84375 5.26042 10.5729C5.98958 11.3021 6.875 11.6667 7.91667 11.6667Z"
                          fill="#808B90"
                        />
                      </svg>
                    }
                  />
                  <ButtonIconGhostMedium
                    active={sortAToZ !== undefined}
                    icon={
                      sortAToZ === 'A' ? (
                        <span style={{ fontWeight: '700', fontSize: '12px' }}>
                          Inc
                        </span>
                      ) : sortAToZ === 'Z' ? (
                        <span style={{ fontWeight: '700', fontSize: '12px' }}>
                          Dec
                        </span>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ flex: '1 0 0', alignSelf: 'stretch' }}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M2 17L5.75 7H7.9L11.65 17H9.6L8.75 14.6H4.9L4.1 17H2ZM5.5 12.9H8.1L6.9 9.15H6.75L5.5 12.9ZM13.7 17V15.1L18.75 8.8H13.9V7H20.95V8.9L15.95 15.2H21V17H13.7ZM9 5L12 2L15 5H9ZM12 22L9 19H15L12 22Z" />
                        </svg>
                      )
                    }
                    onClick={() => {
                      if (sortAToZ === 'A') {
                        setSortAToZ('Z');
                      } else if (sortAToZ === 'Z') {
                        setSortAToZ(undefined);
                      } else setSortAToZ('A');
                    }}
                  />
                </div>
                <div className={`dropdown__list ${noBorder && 'no__border'}`}>
                  {menu}
                </div>
                <div className="footer">
                  <div className="left">
                    {actionLeft?.map((a, i) => (
                      <div key={i}>{a}</div>
                    ))}
                  </div>
                </div>
              </div>
            );
          return <div className={`${noBorder && 'no__border'}`}>{menu}</div>;
        }}
        suffixIcon={<Icon.ICExpandMore />}
        {...props}
      />
    );
  }
);

SelectComponent.displayName = 'SelectComponent';

export default SelectComponent;
